(function() {
    'use strict';

    let generation = 0;
    let cached = null;
    let pending = null;
    let activeContext = null;
    let displayedKey = '';
    let autoRefresh = false;
    let refreshTimer = null;

    function stopRefresh() {
        if (refreshTimer !== null) window.clearInterval(refreshTimer);
        refreshTimer = null;
    }

    function startRefresh() {
        stopRefresh();
        if (!autoRefresh) return;
        refreshTimer = window.setInterval(() => {
            const host = document.getElementById('providerDetailedInsights');
            const panel = document.getElementById('sales-panel');
            if (!activeContext || document.hidden || (panel && !panel.classList.contains('active')) ||
                !host || pending) return;
            // Never discard costs being edited during an automatic refresh.
            if (host.querySelector('[data-cost-form]:not([hidden])')) return;
            window.renderProviderInsights(activeContext, true);
        }, 60000);
    }

    function countSummary(values) {
        return Object.entries(values).map(([label, qty]) => `${label}: ${qty}`).join(', ');
    }

    window.renderProviderInsights = async function(context, force = false) {
        const host = document.getElementById('providerDetailedInsights');
        if (!host) return;
        activeContext = context;
        const key = `${context.providerId}|${context.startDate}|${context.endDate}`;
        if (!force && displayedKey === key && host.querySelector('[data-cost-form]:not([hidden])')) return;
        if (!force && displayedKey === key && cached && Date.now() - cached.at < 60000) return;
        const token = ++generation;
        const escape = context.escape;
        if (!context.startDate || context.days > 366) {
            stopRefresh();
            displayedKey = '';
            host.innerHTML = '<p class="insights-warning">For detailed comparisons, choose 7, 30, 90 days, or a range of up to 366 days. Existing all-time totals remain above.</p>';
            return;
        }
        host.innerHTML = '<p role="status">Loading detailed rankings, costs and preorders...</p>';
        try {
            let data;
            if (!force && cached && cached.key === key && Date.now() - cached.at < 60000) {
                data = cached.data;
            } else {
                if (!pending || pending.key !== key || force) {
                    const promise = window.callProtectedFunction('getProviderInsights', {
                        startDate: context.startDate, endDate: context.endDate
                    });
                    pending = { key, promise };
                }
                const request = pending;
                let result;
                try {
                    result = await request.promise;
                } finally {
                    if (pending === request) pending = null;
                }
                if (!result || !result.ok || !result.insights) throw new Error('The insights service returned no data.');
                data = result.insights;
                if (token === generation) cached = { key, data, at: Date.now() };
            }
            if (token !== generation || activeContext.providerId !== context.providerId) return;
            displayedKey = key;
            draw(host, data, context);
        } catch (error) {
            if (token !== generation) return;
            displayedKey = '';
            console.error('[Insights] Could not load private provider insights:', error);
            host.innerHTML = `<p class="insights-warning" role="alert">Detailed insights unavailable: ${escape(error.message)}. No partial rankings are shown.</p><button type="button" data-retry>Retry insights</button>`;
            host.querySelector('[data-retry]').onclick = () => window.renderProviderInsights(activeContext, true);
        }
    };

    function draw(host, data, context) {
        const escape = context.escape;
        const currency = data.currency;
        const money = (value) => new Intl.NumberFormat(context.locale, { style: 'currency', currency }).format(value);
        const current = data.current;
        const previous = data.previous;
        const amount = (value) => value === null || value === undefined ? 'No transactions' : money(value);
        const aov = current.aov;
        const priorAov = previous.aov;
        const live = data.live;
        const liveRows = live ? live.hours.filter((row) => row.state !== 'future') : [];
        const liveSignal = (row) => {
            if (row.state !== 'complete') return 'Partial hour - no dip comparison';
            if (!data.openHours[new Date(`${live.today}T12:00:00Z`).getUTCDay()][row.hour]) return 'No currently posted open hours';
            if (row.baselinePlaced < 2) return 'Insufficient demand baseline';
            if (row.placed <= row.baselinePlaced * 0.7) return 'Demand dip (30%+ below baseline)';
            if (row.placed >= row.baselinePlaced * 1.3) return 'Demand rush (30%+ above baseline)';
            return 'Near demand baseline';
        };
        const trend = previous.grossSales > 0
            ? `${((current.grossSales - previous.grossSales) / previous.grossSales * 100).toFixed(1)}%`
            : 'No prior sales baseline';
        const warnings = [
            'Standard paid online meals use payment dates. Separate gift-order records are not included. Online item revenue excludes tax and drinks. Hourly demand counts standard orders placed, including cancelled orders and preorders; it is not kitchen workload.',
            'Walk-in rows are separate POS items (matched by name only), without timestamps or historical costs. Their revenue is the item amount entered; enter amounts excluding tax/drinks. No margin is estimated for those rows.',
            'Margins cover recorded ingredient, packaging, portion and extra costs only; they exclude labour, rent, delivery and payment fees. Older orders without cost snapshots stay uncosted.'
        ];
        if (current.timestampFallbacks || previous.timestampFallbacks) warnings.push(
            `${current.timestampFallbacks + previous.timestampFallbacks} paid orders lack payment timestamps; their placement dates were used.`);
        if (current.excludedCurrencies || previous.excludedCurrencies) warnings.push(
            `${current.excludedCurrencies + previous.excludedCurrencies} records use a different currency and are excluded from money rankings.`);
        if (current.unattributedWalkIn > 0) warnings.push(
            `${money(current.unattributedWalkIn)} walk-in net sales lack item attribution and are not ranked.`);

        const quiet = [];
        let peak = 0;
        current.hourly.forEach((row) => row.forEach((value) => { peak = Math.max(peak, value); }));
        current.completeHourly.forEach((row, day) => row.forEach((count, hour) => {
            if (current.observedDays[day] >= 2 && data.openHours[day][hour]) {
                quiet.push({ day, hour, average: count / current.observedDays[day] });
            }
        }));
        quiet.sort((a, b) => a.average - b.average || a.day - b.day || a.hour - b.hour);
        const dayLabel = (day) => context.weekday(day);
        const candidate = current.items.filter((item) => item.menuItemId && item.costCoverage === 100 &&
            item.grossProfit > 0 && data.menu.some((menu) => menu.id === item.menuItemId && menu.isActive))
            .sort((a, b) => b.marginPercent - a.marginPercent)[0];
        const cells = current.hourly.map((row, day) => `<tr><th scope="row">${escape(dayLabel(day))}</th>${row.map((count, hour) => {
            const observed = current.observedDays[day];
            const level = peak ? Math.ceil(4 * count / peak) : 0;
            return `<td class="heat-level-${level}" title="${escape(dayLabel(day))} ${hour}:00: ${count} orders, ${observed} complete days observed${data.openHours[day][hour] ? ', current menu hours open' : ', no posted open hours'}">${count}</td>`;
        }).join('')}</tr>`).join('');
        host.innerHTML = `
            <div class="insights-heading"><h4>Best-seller tracking and planning</h4><button type="button" data-refresh>Refresh insights</button></div>
            <p>Compared with ${escape(data.range.previousStart)} to ${escape(data.range.previousEnd)} (${data.range.days} days).
                Gross receipts: ${money(current.grossSales)} vs ${money(previous.grossSales)}; ${escape(trend)}.
                Today is partial when included.</p>
            ${aov ? `<section aria-label="Average order value">
                <h4>Average order value (AOV)</h4>
                <p>Combined net AOV: <strong>${amount(aov.net)}</strong> vs ${amount(priorAov.net)} previously
                    (${priorAov.net > 0 && aov.net !== null ? `${((aov.net - priorAov.net) / priorAov.net * 100).toFixed(1)}%` : 'No comparison baseline'}).
                    ${aov.transactions} transactions. Gross AOV including tax: ${amount(aov.gross)}.</p>
                <p>Online net AOV: ${amount(aov.onlineNet)} · Walk-in net AOV: ${amount(aov.walkInNet)}.</p>
                <p>Paid online orders with paid drinks/add-ons: ${aov.upsellRate === null ? 'No transactions' : `${aov.upsellRate}%`}.
                    With upsells: ${amount(aov.baskets.withUpsells.netAov)} (${aov.baskets.withUpsells.transactions} orders);
                    without: ${amount(aov.baskets.withoutUpsells.netAov)} (${aov.baskets.withoutUpsells.transactions} orders).</p>
                <p>AOV = receipts / transactions, not spend per unique customer. Net excludes tax but includes drinks/add-ons.
                    Upsell groups show association, not proof of promotion impact; combos are not separately identified.</p>
            </section>` : '<p class="insights-warning">AOV unavailable. Deploy the updated insights backend.</p>'}
            <section aria-label="Live sales trends">
                <h4>Today: live sales trends and hourly dips</h4>
                <label><input type="checkbox" data-auto-refresh ${autoRefresh ? 'checked' : ''}> Refresh every 60 seconds</label>
                <p>Optional near-real-time polling, not instant streaming. Pauses while hidden or editing costs.
                    Use Refresh insights for an immediate update. Never publishes a promotion automatically.</p>
                ${live ? `<p>As of ${escape(new Date(live.updatedAt).toLocaleString(context.locale, { timeZone: data.timezone }))} (${escape(data.timezone)}).
                    Today always loads independently of the selected historical period.
                    Baseline: average of ${live.baselineDays} matching weekdays in the last 28 calendar days, including zero-order days.</p>
                    <p>Today: ${money(liveRows.reduce((sum, row) => sum + row.netReceipts, 0))} net online receipts,
                    ${liveRows.reduce((sum, row) => sum + row.paid, 0)} paid orders,
                    ${liveRows.reduce((sum, row) => sum + row.placed, 0)} orders placed.</p>
                    <div class="insights-scroll"><table class="insights-table" data-live-hours>
                    <thead><tr><th>Hour</th><th>Orders placed</th><th>Baseline orders</th><th>Paid orders</th>
                    <th>Net receipts</th><th>Baseline net receipts</th><th>Demand signal</th></tr></thead>
                    <tbody>${liveRows.map((row) => `<tr><th>${row.hour}:00${row.state === 'partial' ? ' (partial)' : ''}</th>
                    <td>${row.placed}</td><td>${row.baselinePlaced}</td><td>${row.paid}</td>
                    <td>${money(row.netReceipts)}</td><td>${money(row.baselineNetReceipts)}</td><td>${liveSignal(row)}</td></tr>`).join('')}</tbody></table></div>
                    <p>Hourly receipts require actual payment timestamps; ${live.missingPaymentTimes} paid records lack them and are excluded.
                    ${live.excludedCurrencies} receipt records use another currency and are excluded.
                    Demand includes cancelled orders/preorders, not preparation timing. Walk-in daily totals and separate gift orders are not included.
                    Current menu hours are not historical opening records. DST repeated hours share one local-hour bucket.</p>
                    <label>Flash promo candidate <select data-live-promo><option value="">Choose an active meal</option>
                    ${data.menu.filter((item) => item.isActive).map((item) => `<option value="${escape(item.id)}">${escape(item.name)}</option>`).join('')}</select></label>
                    <button type="button" data-live-review>Review flash promo</button>
                    <p>Opens Daily Special for your approval. Check deal margin, stock and current demand before publishing or adjusting staffing.</p>` :
                    '<p class="insights-warning">Live trends unavailable. Deploy the updated insights backend.</p>'}
            </section>
            <details class="insights-coverage" open><summary>Data coverage and calculation notes</summary>
                <ul>${warnings.map((warning) => `<li>${escape(warning)}</li>`).join('')}</ul>
                <p>Server queries cover both selected periods, without using the browser's paginated orders or truncated daily item lists.
                Refunded orders are excluded when their payment status is no longer paid; this is not a refund-date ledger.</p>
            </details>
            <label>Rank by <select data-sort><option value="quantity">Units sold</option><option value="netRevenue">Net meal revenue</option>
                <option value="grossProfit">Recorded gross profit</option><option value="marginPercent">Recorded margin %</option></select></label>
            <div class="insights-scroll"><table class="insights-table"><caption>All items sold in the selected or preceding period</caption>
                <thead><tr><th>Meal</th><th>Units</th><th>Prior units</th><th>Change</th><th>Net revenue</th><th>Revenue share</th>
                <th>Prior revenue</th><th>Revenue change</th><th>Recorded profit</th><th>Margin</th><th>Cost coverage</th><th>Action</th></tr></thead><tbody data-ranking></tbody></table></div>
            <h4>Hourly online demand (${escape(data.timezone)})</h4>
            <p>Counts by weekday and local placement hour. A quiet-hour suggestion requires at least two completed observation days.
                Posted hours reflect the current menu, not a historical opening-hours record.</p>
            <div class="insights-scroll"><table class="insights-heatmap"><caption>Darker cells mean more orders placed</caption>
                <thead><tr><th>Day / hour</th>${Array.from({ length: 24 }, (_, hour) => `<th>${hour}</th>`).join('')}</tr></thead>
                <tbody>${cells}</tbody></table></div>
            <p>${quiet.length ? `Quiet posted-open windows: ${quiet.slice(0, 3).map((slot) =>
                `${escape(dayLabel(slot.day))} ${slot.hour}:00 (${slot.average.toFixed(1)} orders/day)`).join('; ')}.` :
                'Not enough complete days or posted operating hours to suggest a promotion window.'}</p>
            ${candidate && quiet.length ? `<p>Consider ${escape(candidate.name)} (${candidate.marginPercent}% recorded food margin)
                for a promotion in a quiet window. Review stock and deal price before publishing.
                <button type="button" data-promote="${escape(candidate.menuItemId)}">Review Daily Special</button></p>` : ''}
            <h4>Tomorrow's preorder preparation - ${escape(data.tomorrow)}</h4>
            <p>Open orders only, separate from paid historical sales. Pending quantities still await confirmation.
                Counts represent meals to prepare, not ingredient purchase quantities.</p>
            ${data.preparation.length ? `<ul>${data.preparation.map((row) => `<li><strong>${escape(row.name)}</strong>:
                ${row.quantity} meals (${row.confirmedQuantity} confirmed or in progress), ${row.orders} orders.
                Portions: ${escape(countSummary(row.portionCounts))}.
                ${Object.keys(row.extraCounts).length ? `Extras: ${escape(countSummary(row.extraCounts))}.` : ''}
                ${Object.keys(row.drinkCounts).length ? `Drinks: ${escape(countSummary(row.drinkCounts))}.` : ''}</li>`).join('')}</ul>` :
                '<p>No open preorders for tomorrow.</p>'}
            <details><summary>Private menu costs</summary>
                <p>Set base ingredient and packaging costs per serving. Portion costs are additional to the base;
                    paid-extra costs are additional per selected extra. Enter zero explicitly for no added cost.
                    Blank or missing option costs leave affected orders uncosted. Changes apply to new orders only.</p>
                <label>Menu item <select data-cost-menu><option value="">Choose a meal</option>${data.menu.map((item) =>
                    `<option value="${escape(item.id)}">${escape(item.name)}</option>`).join('')}</select></label>
                <form data-cost-form hidden></form>
            </details>`;
        const ranking = host.querySelector('[data-ranking]');
        const rankBy = (field) => {
            const rows = current.items.slice().sort((a, b) =>
                (b[field] === null ? -Infinity : b[field]) - (a[field] === null ? -Infinity : a[field]) ||
                b.quantity - a.quantity);
            ranking.innerHTML = rows.length ? rows.map((row) => `<tr>
                <th scope="row">${escape(row.name)}${row.walkInQuantity ? ' (POS)' : ''}</th><td>${row.quantity}</td>
                <td>${row.previousQuantity}</td><td>${row.quantityChangePercent === null ? 'No baseline' : `${row.quantityChangePercent}%`}</td>
                <td>${money(row.netRevenue)}</td><td>${row.revenueShare}%</td>
                <td>${money(row.previousNetRevenue)}</td><td>${row.revenueChangePercent === null ? 'No baseline' : `${row.revenueChangePercent}%`}</td>
                <td>${row.grossProfit === null ? 'Cost not recorded' : money(row.grossProfit)}</td>
                <td>${row.marginPercent === null ? '-' : `${row.marginPercent}%`}</td><td>${row.costCoverage}%</td>
                <td>${row.menuItemId && data.menu.some((item) => item.id === row.menuItemId && item.isActive) ?
                    `<button type="button" data-promote="${escape(row.menuItemId)}">Review special</button>` : '-'}</td>
                </tr>`).join('') : '<tr><td colspan="12">No paid item sales in these periods.</td></tr>';
        };
        rankBy('quantity');
        host.querySelector('[data-sort]').onchange = (event) => rankBy(event.target.value);
        host.querySelector('[data-refresh]').onclick = () => window.renderProviderInsights(activeContext, true);
        host.querySelector('[data-auto-refresh]').onchange = (event) => {
            autoRefresh = event.target.checked;
            startRefresh();
        };
        if (live) {
            const review = host.querySelector('[data-live-review]');
            const menu = host.querySelector('[data-live-promo]');
            review.disabled = true;
            menu.onchange = () => { review.disabled = !menu.value; };
            review.onclick = () => { if (menu.value) context.promote(menu.value); };
        }
        startRefresh();
        host.onclick = (event) => {
            const button = event.target.closest('[data-promote]');
            if (button) context.promote(button.dataset.promote);
        };
        host.querySelector('[data-cost-menu]').onchange = (event) =>
            drawCostForm(host, data.menu.find((item) => item.id === event.target.value), context, currency);
    }

    function drawCostForm(host, item, context, currency) {
        const form = host.querySelector('[data-cost-form]');
        form.hidden = !item;
        if (!item) return;
        const escape = context.escape;
        const costs = item.costs || {};
        const field = (label, type, key, value) => `<label>${escape(label)}
            <input type="number" min="0" max="100000" step="0.01" data-cost-type="${type}" data-key="${escape(key)}"
                value="${value === undefined ? '' : value}" ${type === 'base' ? 'required' : ''}></label>`;
        const itemCurrency = item.currency || currency;
        form.innerHTML = `<p>Costs in ${escape(itemCurrency)}${costs.currency && costs.currency !== itemCurrency ?
            ` (saved recipe currency: ${escape(costs.currency)})` : ''}</p>` +
            field('Base ingredients / serving', 'base', 'ingredientCost', costs.ingredientCost) +
            field('Packaging / serving', 'base', 'packagingCost', costs.packagingCost) +
            item.portions.map((portion) => field(`Additional cost: ${portion}`, 'portion', portion,
                (costs.portions || []).find((row) => row.key === portion)?.cost)).join('') +
            item.extras.map((extra) => field(`Extra cost: ${extra.name}`, 'extra', extra.key,
                (costs.extras || []).find((row) => row.key === extra.key)?.cost)).join('') +
            `<section class="insights-estimate" aria-label="Meal profitability calculator">
                <h4>Estimated meal profitability per serving</h4>
                <p>Preview uses the costs entered above, including unsaved changes. Selling price excludes tax and drinks.
                    ${item.specialActive ? 'The default price includes the currently active Daily Special.' : 'The default price is the current menu price.'}
                    Testing a price here does not change your menu or publish a special.</p>
                <label>Test base selling price
                    <input type="number" min="0" step="0.01" data-preview-price value="${item.price ?? ''}">
                </label>
                <label>Preview portion <select data-preview-portion>${(item.portions.length ? item.portions : ['Regular'])
                    .map((portion) => `<option value="${escape(portion)}">${escape(portion)}</option>`).join('')}</select></label>
                ${item.extras.map((extra) => `<label><input type="checkbox" data-preview-extra="${escape(extra.key)}">
                    Include ${escape(extra.name)}</label>`).join('')}
                <div data-profit-preview role="status" aria-live="polite"></div>
                <p>Food gross profit = meal selling price - recorded food/packaging costs.
                    Margin = gross profit / meal selling price. This is not net business profit:
                    labour, rent, delivery and payment fees are excluded. Included-component removals do not reduce base cost.</p>
            </section>` +
            '<button type="submit">Save private costs</button><p data-cost-status role="status"></p>';
        const preview = () => {
            const output = form.querySelector('[data-profit-preview]');
            const amount = (input) => input && input.value !== '' && Number.isFinite(Number(input.value)) &&
                Number(input.value) >= 0 && input.validity.valid ? Number(input.value) : null;
            const costFor = (type, key) => amount(Array.from(form.querySelectorAll('[data-cost-type]'))
                .find((input) => input.dataset.costType === type && input.dataset.key === key));
            let cost = costFor('base', 'ingredientCost');
            const packaging = costFor('base', 'packagingCost');
            if (cost === null || packaging === null) {
                output.textContent = 'Cost not recorded: enter valid ingredient and packaging costs, including explicit zero.';
                return;
            }
            cost += packaging;
            let revenue = amount(form.querySelector('[data-preview-price]'));
            const portion = form.querySelector('[data-preview-portion]').value;
            const portionPrice = item.portions.length
                ? (item.portionPrices || []).find((row) => row.key === portion)?.price : 0;
            const portionCost = costFor('portion', portion);
            if (item.portions.length && portionCost === null && portion !== 'Regular') {
                output.textContent = `Cost not recorded: enter the additional cost for ${portion}.`;
                return;
            }
            cost += portionCost ?? 0;
            if (revenue !== null && Number.isFinite(portionPrice)) revenue += portionPrice;
            else revenue = null;
            for (const selected of form.querySelectorAll('[data-preview-extra]:checked')) {
                const extra = item.extras.find((row) => row.key === selected.dataset.previewExtra);
                const extraCost = costFor('extra', extra.key);
                if (extraCost === null) {
                    output.textContent = `Cost not recorded: enter the additional cost for ${extra.name}.`;
                    return;
                }
                cost += extraCost;
                if (revenue !== null && Number.isFinite(extra.price)) revenue += extra.price;
                else revenue = null;
            }
            if (revenue === null) {
                output.textContent = 'Selling price not recorded: enter a valid base price and check menu portion/add-on prices.';
                return;
            }
            const round = (value) => Math.round(value * 100) / 100;
            cost = round(cost);
            revenue = round(revenue);
            const profit = round(revenue - cost);
            const money = (value) => new Intl.NumberFormat(context.locale, { style: 'currency', currency: itemCurrency }).format(value);
            output.textContent = `Estimated meal revenue: ${money(revenue)} | Cost per serving: ${money(cost)} | ` +
                `Estimated food gross profit: ${money(profit)} | Margin: ${revenue > 0 ? `${(100 * profit / revenue).toFixed(2)}%` : 'Not available at zero selling price'}` +
                (profit < 0 ? ' | Warning: this meal sells below its recorded food cost.' : '');
        };
        form.oninput = preview;
        form.onchange = preview;
        preview();
        form.onsubmit = async (event) => {
            event.preventDefault();
            const status = form.querySelector('[data-cost-status]');
            const button = form.querySelector('button');
            button.disabled = true;
            const input = { menuItemId: item.id, portions: [], extras: [] };
            form.querySelectorAll('[data-cost-type]').forEach((field) => {
                if (field.dataset.costType === 'base') input[field.dataset.key] = Number(field.value);
                else if (field.value !== '') input[field.dataset.costType === 'portion' ? 'portions' : 'extras']
                    .push({ key: field.dataset.key, cost: Number(field.value) });
            });
            status.textContent = 'Saving costs...';
            try {
                const result = await window.callProtectedFunction('saveProviderMenuCosts', input);
                if (!result || !result.ok) throw new Error('The cost service did not confirm the save.');
                cached = null;
                displayedKey = '';
                if (activeContext.providerId === context.providerId) {
                    await window.renderProviderInsights(activeContext, true);
                    window.showNotification('Private costs saved. Historical orders were not changed.', 'success');
                }
            } catch (error) {
                console.error('[Insights] Could not save private costs:', error);
                status.textContent = `Costs were not saved: ${error.message}`;
                button.disabled = false;
            }
        };
    }

    window.clearProviderInsights = function() {
        if (typeof window.clearProviderInventory === 'function') window.clearProviderInventory();
        autoRefresh = false;
        stopRefresh();
        generation += 1;
        cached = null;
        pending = null;
        activeContext = null;
        displayedKey = '';
        const host = document.getElementById('providerDetailedInsights');
        if (host) host.replaceChildren();
    };
})();
